import React from 'react';

import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Operation } from 'src/types/operation';

import { ValidationSummaryModal } from './ValidationSummaryModal';
import { CSVUploadSection } from '../../Wallet/components/CSVUploadSection';
import { FundSearchSection } from '../../Wallet/components/FundSearchSection';

type FundSearchModalProps = {
  open: boolean;
  onClose: () => void;
  onFundSelect: (fund: Operation) => void;
  onConfirmImport: () => void;
  onCancelImport: () => void;
  operations: Operation[];
  isLoadingOperations: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  document: File | null;
  setDocument: (file: File | null) => void;
  csvValidation: any;
  setCsvValidation: (validation: any) => void;
  isValidating: boolean;
  setIsValidating: (validating: boolean) => void;
  showValidationSummary: boolean;
  fundValidation: any;
  onInputFocus?: (focused: boolean) => void;
  csvNotFoundCnpjs?: string[];
  csvNonConformingCnpjs?: string[];
  restrictedOperations?: Operation[];
  restrictionMessage?: string;
  isRestrictionLoading?: boolean;
  isRestrictionError?: boolean;
  onRetryRestriction?: () => void;
  isSelectionBlocked?: boolean;
};

export function FundSearchModal({
  open,
  onClose,
  onFundSelect,
  onConfirmImport,
  onCancelImport,
  operations,
  isLoadingOperations,
  searchValue,
  onSearchChange,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  document,
  setDocument,
  csvValidation,
  setCsvValidation,
  isValidating,
  setIsValidating,
  showValidationSummary,
  fundValidation,
  onInputFocus,
  csvNotFoundCnpjs = [],
  csvNonConformingCnpjs = [],
  restrictedOperations,
  restrictionMessage,
  isRestrictionLoading = false,
  isRestrictionError = false,
  onRetryRestriction,
  isSelectionBlocked = false,
}: FundSearchModalProps) {
  const hasOperationRestriction = restrictedOperations !== undefined;
  const isRestrictedAddition = hasOperationRestriction || isSelectionBlocked;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        Adição de Cotas
        <IconButton
          aria-label="Fechar modal de adição de cotas"
          onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <>
          {isRestrictionLoading && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              role="status"
              aria-live="polite"
              aria-label="Verificando os fundos permitidos para esta carteira">
              <CircularProgress
                size="1.25rem"
                aria-label="Verificando os fundos permitidos para esta carteira"
              />
              <Typography variant="body2" color="text.secondary">
                Verificando os fundos permitidos para esta carteira...
              </Typography>
            </Box>
          )}
          {isRestrictionError && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                onRetryRestriction ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onRetryRestriction}>
                    Tentar novamente
                  </Button>
                ) : undefined
              }>
              Não foi possível validar os fundos permitidos para esta carteira.
              Tente novamente para continuar a adição.
            </Alert>
          )}
          {restrictionMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {restrictionMessage}
            </Typography>
          )}
          {!isSelectionBlocked && (
            <FundSearchSection
              operations={restrictedOperations ?? operations}
              isLoadingOperations={
                hasOperationRestriction ? false : isLoadingOperations
              }
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              onFundSelect={onFundSelect}
              onLoadMore={hasOperationRestriction ? undefined : onLoadMore}
              hasNextPage={hasOperationRestriction ? false : hasNextPage}
              isFetchingNextPage={
                hasOperationRestriction ? false : isFetchingNextPage
              }
              onInputFocus={onInputFocus}
              restrictSearch={hasOperationRestriction}
            />
          )}

          {!isRestrictedAddition && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Ou
                </Typography>
              </Divider>

              <CSVUploadSection
                document={document}
                setDocument={setDocument}
                csvValidation={csvValidation}
                setCsvValidation={setCsvValidation}
                isValidating={isValidating}
                setIsValidating={setIsValidating}
              />
            </>
          )}

          {!isRestrictedAddition && (
            <ValidationSummaryModal
              open={showValidationSummary}
              onClose={onCancelImport}
              onConfirm={onConfirmImport}
              onCancel={onCancelImport}
              validation={
                fundValidation || {
                  newFunds: [],
                  existingFundUpdates: [],
                  duplicates: [],
                  notFoundFunds: [],
                }
              }
              notFoundCnpjs={csvNotFoundCnpjs}
              nonConformingCnpjs={csvNonConformingCnpjs}
              hasErrors={
                fundValidation
                  ? fundValidation.duplicates.length > 0 ||
                    fundValidation.notFoundFunds.length > 0
                  : false
              }
            />
          )}
        </>
      </DialogContent>
    </Dialog>
  );
}
